/**
 * A customer's administrators who haven't accepted their invitation yet, with
 * "Resend invitation". The invitation goes only to their inbox (staff never
 * see the link); resending voids the earlier link. If it keeps failing, Support
 * shows why emails to that address don't arrive.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { Button } from '@lipaflow/ui'
import { apiErrorMessage } from '@lipaflow/shared'
import { adminService } from '../../services/admin'

export function AdminInvitationSection({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient()
  const users = useQuery({ queryKey: ['customer-users', tenantId], queryFn: () => adminService.listTenantUsers(tenantId) })
  const resend = useMutation({
    mutationFn: (email: string) => adminService.resendAdminInvitation(tenantId, email),
    onSuccess: (r) => {
      if (r.delivery_status === 'sent') toast.success(`Invitation emailed to ${r.email}`)
      else toast.error(`The email to ${r.email} failed${r.delivery_error ? `: ${r.delivery_error}` : ''}. Check the address.`)
      queryClient.invalidateQueries({ queryKey: ['customer-users', tenantId] })
    },
    onError: (e) => toast.error(apiErrorMessage(e, "Couldn't resend the invitation")),
  })

  const waiting = (users.data ?? []).filter((u) => u.is_administrator && u.is_active && !u.is_verified)
  if (waiting.length === 0) return null

  return (
    <section aria-labelledby="customer-invitations" className="rounded-card border border-hairline p-4 space-y-2">
      <h4 id="customer-invitations" className="font-inter text-caption text-ink-muted">Invitation not accepted yet</h4>
      <ul className="space-y-2">
        {waiting.map((u) => (
          <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 text-body-sm">
            <span className="text-ink">{u.full_name || u.email} <span className="text-ink-muted">· {u.email}</span></span>
            <Button size="sm" variant="secondary" isLoading={resend.isPending && resend.variables === u.email}
              disabled={resend.isPending} onClick={() => resend.mutate(u.email)}>Resend invitation</Button>
          </li>
        ))}
      </ul>
      <p className="text-caption text-ink-muted">It goes to their inbox only; the earlier link stops working.</p>
    </section>
  )
}
